import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const TABS = [
  { id: "orders", label: "Commandes" },
  { id: "messages", label: "Messages" },
  { id: "subscribers", label: "Abonnés" },
];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [data, setData] = useState({ orders: null, messages: null, subscribers: null });

  useEffect(() => {
    if (user === false) navigate("/auth");
    else if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      TABS.forEach((t) => {
        api
          .get(`/admin/${t.id}`)
          .then((res) => setData((d) => ({ ...d, [t.id]: res.data })))
          .catch(() => setData((d) => ({ ...d, [t.id]: [] })));
      });
    }
  }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-100" />
      </div>
    );
  }

  const orders = data.orders || [];
  const messages = data.messages || [];
  const subscribers = data.subscribers || [];
  const revenue = orders.filter((o) => o.status === "paid").reduce((s, o) => s + (o.total || 0), 0);

  const stats = [
    { label: "Commandes", value: orders.length, testid: "stat-orders" },
    { label: "Revenus (payés)", value: `€${revenue.toFixed(0)}`, testid: "stat-revenue" },
    { label: "Messages", value: messages.length, testid: "stat-messages" },
    { label: "Abonnés", value: subscribers.length, testid: "stat-subscribers" },
  ];

  return (
    <div className="mx-auto max-w-[1700px] px-5 pb-28 pt-36 sm:px-8 lg:px-16 lg:pt-44" data-testid="admin-page">
      <Reveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">La Maison — Interne</p>
        <h1 className="mt-4 font-blackletter text-6xl leading-[1] sm:text-7xl lg:text-8xl">Dashboard</h1>
      </Reveal>

      <Reveal delay={0.1} className="mt-14 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="border border-white/10 bg-coal p-6" data-testid={s.testid}>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">{s.label}</p>
            <p className="mt-3 font-display text-3xl font-bold">{s.value}</p>
          </div>
        ))}
      </Reveal>

      <Reveal delay={0.15} className="mt-12 flex flex-wrap gap-2 border-t border-white/5 pt-8">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
              tab === t.id
                ? "border-white bg-white text-ink"
                : "border-white/10 text-zinc-500 hover:border-zinc-400 hover:text-zinc-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </Reveal>

      <div className="mt-10">
        {data[tab] === null ? (
          <p className="font-mono text-xs text-zinc-600">Chargement…</p>
        ) : tab === "orders" ? (
          orders.length === 0 ? (
            <p className="font-serif text-xl italic text-zinc-400" data-testid="admin-orders-empty">
              Aucune commande.
            </p>
          ) : (
            <ul className="space-y-4" data-testid="admin-orders-list">
              {orders.map((o) => (
                <li key={o.id} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-coal p-6">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono text-xs tracking-wider text-zinc-200">
                        #{o.id.slice(0, 8).toUpperCase()}
                      </span>
                      <span
                        className={`px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] ${
                          o.status === "paid" ? "bg-white text-ink" : "border border-white/20 text-zinc-400"
                        }`}
                      >
                        {o.status === "paid" ? "Payée" : "En attente"}
                      </span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">
                        {o.provider || "stripe"}
                      </span>
                    </div>
                    <p className="mt-3 font-mono text-[10px] text-zinc-500">
                      {o.items?.map((i) => `${i.name} (${i.size}) ×${i.quantity}`).join(" · ")}
                    </p>
                    <p className="mt-1 font-mono text-[10px] text-zinc-600">{o.customer_email || "invité"}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-xl font-bold">€{o.total?.toFixed(0)}</p>
                    <p className="mt-1 font-mono text-[9px] text-zinc-600">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString("fr-FR") : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )
        ) : tab === "messages" ? (
          messages.length === 0 ? (
            <p className="font-serif text-xl italic text-zinc-400" data-testid="admin-messages-empty">
              Aucun message.
            </p>
          ) : (
            <ul className="space-y-4" data-testid="admin-messages-list">
              {messages.map((m) => (
                <li key={m.id} className="border border-white/10 bg-coal p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-sm font-semibold uppercase">{m.name}</span>
                      <span className="border border-white/20 px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-400">
                        {m.subject}
                      </span>
                    </div>
                    <span className="font-mono text-[9px] text-zinc-600">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : ""}
                    </span>
                  </div>
                  <p className="mt-2 font-mono text-[10px] text-zinc-500">{m.email}</p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-300">{m.message}</p>
                </li>
              ))}
            </ul>
          )
        ) : subscribers.length === 0 ? (
          <p className="font-serif text-xl italic text-zinc-400" data-testid="admin-subscribers-empty">
            Aucun abonné.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-subscribers-list">
            {subscribers.map((s) => (
              <li key={s.id} className="flex items-center justify-between border border-white/10 bg-coal px-5 py-4">
                <span className="truncate font-mono text-[11px] text-zinc-300">{s.email}</span>
                <span className="ml-3 shrink-0 font-mono text-[9px] text-zinc-600">
                  {s.created_at ? new Date(s.created_at).toLocaleDateString("fr-FR") : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
